import { deepBlueSky } from "@/styles/theme";
import {
  Box,
  Button,
  Divider,
  Flex,
  FlexProps,
  Heading,
  HStack,
  List,
  ListItem,
  Tag,
  Text,
} from "@chakra-ui/react";
import { DEFAULT_FREE_TRIAL_DAYS } from "@root/types/freeTrial";
import currency from "currency.js";
import { useTranslations } from "next-intl";
import { FieldValues, UseFormWatch } from "react-hook-form";

const variants = {
  canary: {
    bg: "brand.900",
    color: "brand.1000",
    borderColor: "brand.900",
    dividerColor: "brand.1000",
    buttonVariant: "secondary",
    tagBg: "brand.800",
  },
  blue: {
    bg: deepBlueSky,
    color: "white",
    borderColor: deepBlueSky,
    dividerColor: "white",
    buttonVariant: "secondary",
    tagBg: "brand.900",
  },
  black: {
    bg: "brand.1000",
    color: "white",
    borderColor: "brand.1000",
    dividerColor: "white",
    buttonVariant: "primary",
    tagBg: "brand.900",
  },
};

export const PricingBox = ({
  price,
  name,
  description,
  header,
  oldPrice,
  additionalCoachPrice,
  features,
  variant,
  platformId,
  freeTrialDays = DEFAULT_FREE_TRIAL_DAYS,
  onSelect,
  watch,
  cardProps,
}: {
  price: string;
  name: string;
  description: string;
  additionalCoachPrice?: string;
  header?: string;
  oldPrice?: string;
  features?: string[];
  variant: keyof typeof variants;
  platformId: string;
  freeTrialDays?: number;
  onSelect?: () => void;
  watch?: UseFormWatch<FieldValues>;
  cardProps?: FlexProps;
}) => {
  const t = useTranslations();
  const { bg, color, borderColor, dividerColor, buttonVariant, tagBg } =
    variants[variant];
  const selectedPlan = watch?.("selectedPlan");
  const selected = selectedPlan?.toString() === platformId.toString();
  return (
    <Flex
      as="a"
      href={onSelect ? undefined : `/auth/sign-up?plan=${platformId}`}
      direction="column"
      cursor="pointer"
      flex={1}
      rounded="12px"
      bg={bg}
      color={color}
      border={selected ? "1px solid" : "none"}
      borderColor={borderColor}
      p={8}
      transition="all 0.3s ease-in-out"
      transform={selected ? "translateY(-2px)" : "none"}
      _hover={{
        transform: "translateY(-2px)",
      }}
      {...cardProps}
    >
      <Box w="full" flex={1}>
        <Box textAlign="left">
          <Heading fontSize="1.75rem">{t(`plans.${name}`)}</Heading>
          <HStack justifyContent="space-between">
            <Text>{description}</Text>
          </HStack>
        </Box>

        <Box my={6}>
          <Divider borderColor={dividerColor} />
        </Box>

        {!!additionalCoachPrice && (
          <HStack justifyContent="space-between" mb={2}>
            <Text>Additional Team Coach</Text>
            <Text size="sm">+ {additionalCoachPrice}</Text>
          </HStack>
        )}
        <List mb={12} listStyleType={"'- '"} textAlign="left" pl={4}>
          {features?.map((feature) => (
            <ListItem key={feature}>{t(feature)}</ListItem>
          ))}
        </List>
      </Box>
      <Box mb={4}>
        {!!oldPrice && (
          <Text textDecoration={"line-through"}>
            {t("before")} {oldPrice}
          </Text>
        )}
        <Heading fontWeight={500} fontFamily={"Gotham"}>
          {currency(price).format({
            symbol: "€",
            precision: 2,
          })}
        </Heading>
      </Box>
      <HStack justifyContent="space-between" w="full">
        <Button
          flex={1}
          rounded="full"
          w={{
            base: "full",
            md: "auto",
          }}
          variant={buttonVariant}
          _hover={{
            bgColor: "brand.800",
            color: "brand.1000",
          }}
          fontWeight={600}
          onClick={onSelect}
          textTransform={"capitalize"}
        >
          {selected ? t("selected") : t("choose")}
        </Button>
        <Box w="120px" textAlign="right">
          <Tag variant={buttonVariant} fontWeight={600}>
            {t("xFreeTrial", { days: freeTrialDays })}
          </Tag>
          {selected && (
            <Tag bgColor={tagBg} color="brand.1000" size="sm" fontWeight={600}>
              {t("selected")}
            </Tag>
          )}
        </Box>
      </HStack>
    </Flex>
  );
};
